import { useRef, useState } from "react";

import { useDispatch } from "react-redux";

import Sentry from "@similarweb/sentry-logger";

import { customSegmentCreatedUpdated } from "app/actions/segmentsModuleActions";
import { showErrorToast } from "app/actions/toast_actions";
import { useOnEscape } from "app/custom-hooks/useOnKeyUp";
import type { ICustomSegment } from "app/services/segments/segmentsApiService";
import type { Step } from "app/components/React/TableSelectionComponents/segment-selection/segments-modal/stepsComponents";
import { translate } from "app/filters/i18nFilter";
import { MODE } from "app/pages/segments/analysis/SegmentsAnalysisTrafficContainerMode";
import {
    generateUniqueName,
    validateSegmentName,
} from "app/pages/segments/redesigned-wizard/segment-wizard/utils/validateSegmentName";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import SegmentsApiService from "app/services/segments/segmentsWizardService";
import { allTrackers } from "app/services/track/track";
import type { UserAssetName } from "app/UtilitiesAndConstants/types/userAssetName";
import { getDefaultValueHof } from "app/UtilitiesAndConstants/types/userAssetName";

export const useSegmentCreation = (currentDomain: string, selectedFoldersForSegment: string[]) => {
    const dispatch = useDispatch();

    const [segmentName, setSegmentName] = useState<UserAssetName>(
        getDefaultValueHof(generateUniqueName(`${currentDomain} segment`)),
    );
    const [isCreatingSegment, setIsCreatingSegment] = useState(false);
    const [isCreateNewListModalOpen, setIsCreateNewListModalOpen] = useState(false);
    const [popupStep, setPopupStep] = useState<Step>("Wizard");

    const segmentCreated = useRef<ICustomSegment>(null);

    const onSegmentNameChange = (assetName: string) => {
        const { isValid: isValidName, errorMsg: errorMessage } = validateSegmentName(assetName);
        setSegmentName({ assetName, isValidName, errorMessage });
    };
    const onSave = async () => {
        const { isValidName, assetName } = segmentName;
        if (!isValidName) {
            return;
        }

        allTrackers.dispatchTrackEvent("Pop Up", "click", "save");
        setIsCreatingSegment(true);

        try {
            const segmentsApiService = new SegmentsApiService();
            const rules = [{ words: [], folders: selectedFoldersForSegment, type: 0 }];

            const customSegment = await segmentsApiService.createCustomSegment({
                domain: currentDomain,
                rules,
                segmentName: assetName,
            });

            segmentCreated.current = customSegment;
            dispatch(customSegmentCreatedUpdated(customSegment));
            setPopupStep("Checkout");
        } catch (error) {
            dispatch(showErrorToast(translate("segments.popup.wizard.save.error")));
            Sentry.error("error while creating a segment from a table", error);
        } finally {
            setIsCreatingSegment(false);
        }
    };

    const onNewListButtonClick = () => {
        allTrackers.dispatchTrackEvent("Button", "click", "create new segment from table");
        setIsCreateNewListModalOpen(true);
    };

    const onCancel = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", "cancel segment creation");
        setIsCreateNewListModalOpen(false);
    };

    const closeModalAndReset = () => {
        segmentCreated.current = null;
        setIsCreateNewListModalOpen(false);
        setSegmentName(getDefaultValueHof(generateUniqueName(`${currentDomain} segment`)));
        setPopupStep("Wizard");
    };

    const goBack = () => {
        allTrackers.dispatchTrackEvent("Pop Up", "click", "go back to table");
        closeModalAndReset();
    };

    const analyzeSegment = () => {
        if (!segmentCreated?.current?.id) {
            return;
        }

        allTrackers.dispatchTrackEvent("Pop Up", "click", "analyse segment");
        const { id } = segmentCreated.current;
        const { country, duration } = proNavigator.getParams();

        closeModalAndReset();
        proNavigator.go("digitalsuite_segments-analysis-traffic", {
            mode: MODE.single,
            id,
            country,
            duration,
        });
    };

    useOnEscape(closeModalAndReset);

    return {
        segmentName,
        isCreatingSegment,
        isCreateNewListModalOpen,
        popupStep,
        onSegmentNameChange,
        onSave,
        onNewListButtonClick,
        onCancel,
        goBack,
        analyzeSegment,
    };
};
